import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDocTitle } from '../hooks/useDocTitle';
import { news } from '../data/news';
import Footer from '../sections/Footer';

const gridAnim = keyframes`from{background-position:0 0}to{background-position:0 60px}`;
const fadeUp   = keyframes`from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:none}`;
const blink    = keyframes`0%,100%{opacity:1}50%{opacity:.2}`;

const Page = styled.div`min-height:100vh;overflow-x:hidden;background:#02020E;padding-top:72px;`;
const GridBg = styled.div`
  position:fixed;inset:0;pointer-events:none;z-index:0;
  background-image:
    linear-gradient(rgba(0,163,255,.02) 1px,transparent 1px),
    linear-gradient(90deg,rgba(0,163,255,.02) 1px,transparent 1px);
  background-size:60px 60px;animation:${gridAnim} 22s linear infinite;
`;

const Hero = styled.div`
  position:relative;z-index:2;padding:60px 80px 48px;
  border-bottom:1px solid rgba(255,255,255,.05);
  @media(max-width:900px){padding:48px 24px 36px}
  @media(max-width:480px){padding:32px 16px 28px}
`;
const HeroInner = styled.div`max-width:1360px;margin:0 auto;animation:${fadeUp} .5s ease both`;
const Label = styled.div`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.3em;
  color:rgba(0,163,255,.6);text-transform:uppercase;margin-bottom:16px;
  &::before{content:'◆ '}
`;
const PageTitle = styled.h1`
  font-family:'Bebas Neue',sans-serif;
  font-size:clamp(56px,10vw,120px);line-height:.88;margin-bottom:18px;
  span{background:linear-gradient(135deg,#00A3FF,#C800FF);-webkit-background-clip:text;-webkit-text-fill-color:transparent}
`;
const PageDesc = styled.p`
  font-family:'Rajdhani',sans-serif;font-size:15px;letter-spacing:.06em;
  color:rgba(255,255,255,.4);max-width:520px;line-height:1.7;
`;

const Section = styled.div`
  position:relative;z-index:2;max-width:1360px;margin:0 auto;
  padding:56px 80px 80px;
  @media(max-width:900px){padding:40px 24px 60px}
  @media(max-width:480px){padding:28px 16px 48px}
`;

/* Featured */
const Featured = styled(Link)`
  display:grid;grid-template-columns:1fr 1fr;gap:0;
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);
  margin-bottom:3px;overflow:hidden;transition:all .35s;text-decoration:none;
  &:hover{border-color:rgba(0,163,255,.3);box-shadow:0 20px 60px rgba(0,0,0,.5);}
  @media(max-width:700px){grid-template-columns:1fr}
`;
const FeatViz = styled.div`
  min-height:280px;position:relative;overflow:hidden;
  background:linear-gradient(135deg,rgba(0,163,255,.1),rgba(200,0,255,.08));
  border-right:1px solid rgba(255,255,255,.05);
  display:flex;align-items:center;justify-content:center;font-size:90px;
  &::before{content:'';position:absolute;inset:0;background:repeating-linear-gradient(45deg,rgba(0,163,255,.03) 0,rgba(0,163,255,.03) 1px,transparent 1px,transparent 14px)}
  @media(max-width:700px){min-height:160px;border-right:none;border-bottom:1px solid rgba(255,255,255,.05)}
`;
const FeatBody = styled.div`
  padding:44px 40px;display:flex;flex-direction:column;
  @media(max-width:700px){padding:28px 24px}
  @media(max-width:480px){padding:22px 16px}
`;

/* Grid of cards */
const CardGrid = styled.div`
  display:grid;grid-template-columns:repeat(3,1fr);gap:3px;
  @media(max-width:900px){grid-template-columns:1fr 1fr}
  @media(max-width:560px){grid-template-columns:1fr}
`;
const Card = styled(Link)`
  background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.07);
  padding:28px 22px;display:flex;flex-direction:column;gap:12px;
  @media(max-width:480px){padding:22px 16px}
  text-decoration:none;transition:all .35s;position:relative;overflow:hidden;
  &::after{content:'';position:absolute;bottom:0;left:0;height:2px;width:0;
    background:linear-gradient(135deg,#00A3FF,#C800FF);transition:width .4s}
  &:hover{border-color:rgba(0,163,255,.2);transform:translateY(-4px);box-shadow:0 16px 40px rgba(0,0,0,.5)}
  &:hover::after{width:100%}
`;

/* Shared elements */
const CatBadge = styled.span`
  display:inline-flex;align-items:center;gap:6px;
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.26em;
  color:${p=>p.$c};border:1px solid ${p=>p.$c}33;padding:3px 10px;text-transform:uppercase;
  &::before{content:'';width:5px;height:5px;background:${p=>p.$c};border-radius:50%;animation:${blink} 2s infinite}
`;
const DateStr = styled.div`
  font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.18em;
  color:rgba(255,255,255,.22);text-transform:uppercase;
`;
const CardTitle = styled.h2`
  font-family:'Bebas Neue',sans-serif;letter-spacing:.04em;color:#fff;
  font-size:${p=>p.$lg?'38px':'24px'};line-height:1.1;
  transition:color .3s;
  ${Featured}:hover &,${Card}:hover &{color:#00A3FF}
`;
const Excerpt = styled.p`font-size:14px;line-height:1.74;color:rgba(255,255,255,.45);flex:1;`;
const ReadTime = styled.span`font-family:'Space Mono',monospace;font-size:9px;letter-spacing:.15em;color:rgba(255,255,255,.2)`;
const ReadMore = styled.div`
  font-family:'Space Mono',monospace;font-size:10px;letter-spacing:.22em;
  color:#00A3FF;text-transform:uppercase;margin-top:auto;
  display:flex;align-items:center;gap:6px;
  span{transition:transform .3s}
  ${Featured}:hover span,${Card}:hover span{transform:translateX(5px)}
`;

const EMOJI_MAP = {'Estúdio':'🏢','Jogos':'🎮','Desenvolvimento':'⚙️','XR':'🥽','Lançamento':'🚀','Comunidade':'💬'};

export default function NewsPage() {
  const { t } = useTranslation();
  useDocTitle('pageTitles.news');
  const [featured, ...rest] = news;

  return (
    <Page>
      <GridBg/>
      <Hero>
        <HeroInner>
          <Label>{t('newsPage.sectionLabel')}</Label>
          <PageTitle>{t('newsPage.titleLine1')} <span>{t('newsPage.titleAccent')}</span></PageTitle>
          <PageDesc>{t('newsPage.desc')}</PageDesc>
        </HeroInner>
      </Hero>

      <Section>
        {/* Featured card */}
        <Featured to={`/news/${featured.id}`}>
          <FeatViz>{EMOJI_MAP[featured.category] || '📰'}</FeatViz>
          <FeatBody>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14,flexWrap:'wrap',gap:8}}>
              <CatBadge $c={featured.categoryColor}>{featured.category}</CatBadge>
              <ReadTime>{t('newsPage.readTime', { minutes: featured.readTime })}</ReadTime>
            </div>
            <DateStr style={{marginBottom:14}}>{featured.date}</DateStr>
            <CardTitle $lg>{featured.title}</CardTitle>
            <Excerpt style={{marginTop:14}}>{featured.excerpt}</Excerpt>
            <ReadMore style={{marginTop:24}}>{t('newsPage.readMore')} <span>→</span></ReadMore>
          </FeatBody>
        </Featured>

        {/* Rest as grid */}
        <CardGrid>
          {rest.map(item => (
            <Card key={item.id} to={`/news/${item.id}`}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
                <CatBadge $c={item.categoryColor}>{item.category}</CatBadge>
                <ReadTime>{t('newsPage.readTime', { minutes: item.readTime })}</ReadTime>
              </div>
              <DateStr>{item.date}</DateStr>
              <CardTitle>{item.title}</CardTitle>
              <Excerpt>{item.excerpt}</Excerpt>
              <ReadMore>{t('newsPage.readMore')} <span>→</span></ReadMore>
            </Card>
          ))}
        </CardGrid>
      </Section>

      <Footer/>
    </Page>
  );
}
